// src/features/automacaodeemails/pages/LogsPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";

const ICONS = {
  check: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  ),
  x: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  clock: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12,6 12,12 16,14" />
    </svg>
  ),
  refresh: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23,4 23,10 17,10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
};

const STATUS_CONFIG = {
  SENT: { icon: ICONS.check, label: "Enviado", class: "success" },
  FAILED: { icon: ICONS.x, label: "Falhou", class: "danger" },
  QUEUED: { icon: ICONS.clock, label: "Na fila", class: "warning" },
};

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", days: 7 });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;

      const [logsRes, statsRes] = await Promise.all([
        api.logs.list(params),
        api.logs.stats(filter.days),
      ]);
      setLogs(logsRes?.data || []);
      setStats(statsRes?.data || null);
    } catch (err) {
      console.error("Erro ao carregar logs:", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="ae2-page">
      <div className="ae2-page-header">
        <div>
          <h2>Histórico de envios</h2>
          <p>Monitore entregas, falhas e desempenho do envio.</p>
        </div>
        <div className="ae2-actions">
          <button className="ae2-btn ae2-btn-ghost" onClick={loadLogs}>
            {ICONS.refresh} Atualizar
          </button>
        </div>
      </div>

      {stats && (
        <div className="ae2-stats-bar">
          <div className="ae2-stat-pill">
            <strong>{stats.general?.total || 0}</strong>
            <span>Total ({filter.days} dias)</span>
          </div>
          <div className="ae2-stat-pill success">
            <strong>{stats.general?.sent || 0}</strong>
            <span>Enviados</span>
          </div>
          <div className="ae2-stat-pill danger">
            <strong>{stats.general?.failed || 0}</strong>
            <span>Falhas</span>
          </div>
          <div className="ae2-stat-pill">
            <strong>{toNumber(stats.general?.avg_size_kb).toFixed(1)} KB</strong>
            <span>Tamanho medio</span>
          </div>
        </div>
      )}

      <div className="ae2-filters">
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">Todos os status</option>
          <option value="SENT">Enviados</option>
          <option value="FAILED">Falhas</option>
          <option value="QUEUED">Na fila</option>
        </select>
        <select
          value={filter.days}
          onChange={(e) => setFilter({ ...filter, days: parseInt(e.target.value) })}
        >
          <option value="7">Ultimos 7 dias</option>
          <option value="30">Ultimos 30 dias</option>
          <option value="90">Ultimos 90 dias</option>
        </select>
      </div>

      {loading ? (
        <div className="ae2-loading">Carregando...</div>
      ) : logs.length === 0 ? (
        <div className="ae2-empty">
          <h3>Nenhum log encontrado</h3>
          <p>Os envios de e-mail serao registrados aqui.</p>
        </div>
      ) : (
        <div className="ae2-card ae2-table-wrap">
          <table className="ae2-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Automação</th>
                <th>Status</th>
                <th>Destinatários</th>
                <th>Elementos</th>
                <th>Tamanho</th>
                <th>Tempo</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const statusCfg = STATUS_CONFIG[log.status] || STATUS_CONFIG.QUEUED;
                const dest = Array.isArray(log.destinatarios_enviados) ? log.destinatarios_enviados : [];
                return (
                  <tr key={log.id}>
                    <td>{new Date(log.sent_at).toLocaleString("pt-BR")}</td>
                    <td>{log.automation_nome}</td>
                    <td>
                      <span className={`ae2-badge ${statusCfg.class}`}>
                        {statusCfg.icon} {statusCfg.label}
                      </span>
                    </td>
                    <td>
                      <span title={dest.join(", ")}>{dest.length}</span>
                    </td>
                    <td>{log.elements_count || 0}</td>
                    <td>{toNumber(log.email_size_kb).toFixed(1)} KB</td>
                    <td>
                      {log.capture_duration_ms && log.send_duration_ms
                        ? `${((toNumber(log.capture_duration_ms) + toNumber(log.send_duration_ms)) / 1000).toFixed(1)}s`
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {stats?.common_errors?.length > 0 && (
        <div className="ae2-card ae2-errors">
          <div className="ae2-card-header">
            <h3>Erros mais comuns</h3>
          </div>
          <div className="ae2-errors-list">
            {stats.common_errors.map((err, i) => (
              <div key={i} className="ae2-error-row">
                <span className="ae2-error-code">{err.error_code}</span>
                <span className="ae2-error-msg">{err.error_message}</span>
                <span className="ae2-error-count">{err.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
